/**
 * Opportunity Map HTML builder.
 * 
 * Focused map for a single opportunity showing:
 * - Parcel boundaries (primary + L1/L2/L3 neighbors)
 * - Geo-pinned photos, voice notes, and field notes as markers
 * - Click popups with media content
 * - Layer toggle panel
 * 
 * Communication via postMessage:
 * IN:  set_parcels, set_media_pins, toggle_layer, fly_to
 * OUT: map_ready, media_pin_click, parcel_click
 */

import { BASEMAP_STYLES } from './mapHtmlBuilder';

interface BuildOpportunityMapParams {
  mapboxToken: string;
  center: [number, number];
  zoom: number;
  basemapStyle?: string;
}

export function buildOpportunityMapHtml(params: BuildOpportunityMapParams): string {
  const { mapboxToken, center, zoom, basemapStyle } = params;
  const styleUrl = basemapStyle || BASEMAP_STYLES['satellite-streets'];

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="initial-scale=1,maximum-scale=1,user-scalable=no">
  <link href="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css" rel="stylesheet">
  <script src="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js"></script>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    #map { position: absolute; top: 0; bottom: 0; width: 100%; }
    .mapboxgl-ctrl-attrib, .mapboxgl-ctrl-logo { display: none !important; }
    .mapboxgl-ctrl-group { display: none !important; }

    .layer-panel {
      position: absolute; top: 12px; right: 12px; z-index: 10;
      background: rgba(255,255,255,0.95); border-radius: 10px;
      padding: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); min-width: 150px;
    }
    .layer-panel.collapsed { min-width: auto; padding: 4px; }
    .layer-panel.collapsed .layer-items { display: none; }
    .layer-toggle-btn {
      display: flex; align-items: center; justify-content: center;
      width: 32px; height: 32px; border: none; background: none; cursor: pointer;
      border-radius: 6px; font-size: 18px;
    }
    .layer-toggle-btn:hover { background: rgba(0,0,0,0.05); }
    .layer-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
    .layer-title { font-size: 11px; font-weight: 600; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
    .layer-item {
      display: flex; align-items: center; gap: 8px; padding: 5px 4px;
      cursor: pointer; border-radius: 6px; font-size: 13px; color: #334155;
    }
    .layer-item:hover { background: rgba(0,0,0,0.04); }
    .layer-dot { width: 10px; height: 10px; border-radius: 5px; flex-shrink: 0; }
    .layer-item.off { opacity: 0.4; }
    .layer-item.off .layer-dot { opacity: 0.3; }

    .mapboxgl-popup-content {
      padding: 0 !important; border-radius: 10px !important; overflow: hidden;
      max-width: 280px; box-shadow: 0 4px 16px rgba(0,0,0,0.2) !important;
    }
    .popup-card { padding: 12px; }
    .popup-type { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 4px; }
    .popup-title { font-size: 14px; font-weight: 600; color: #0F172A; margin-bottom: 4px; }
    .popup-body { font-size: 13px; color: #475569; line-height: 1.4; }
    .popup-meta { font-size: 11px; color: #94A3B8; margin-top: 6px; }
    .popup-img { width: 100%; max-height: 160px; object-fit: cover; display: block; }
    .popup-category { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; margin-bottom: 4px; }
    .popup-actions { display: flex; gap: 8px; margin-top: 8px; }
    .popup-btn {
      flex: 1; padding: 6px; border: none; border-radius: 6px; font-size: 12px;
      font-weight: 600; cursor: pointer; text-align: center;
    }
    .popup-btn-primary { background: #16A34A; color: white; }
    .popup-btn-danger { background: #FEE2E2; color: #DC2626; }
    .popup-btn:hover { opacity: 0.85; }

    .media-marker {
      width: 32px; height: 32px; border-radius: 50%; display: flex;
      align-items: center; justify-content: center; cursor: pointer;
      border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      font-size: 15px; transition: transform 0.15s;
    }
    .media-marker:hover { transform: scale(1.15); }
    .media-marker.photo { background: #3B82F6; }
    .media-marker.voice { background: #8B5CF6; }
    .media-marker.note { background: #F59E0B; }

    .tool-panel {
      position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); z-index: 10;
      display: flex; gap: 8px; background: rgba(255,255,255,0.95); border-radius: 12px;
      padding: 6px 10px; box-shadow: 0 2px 12px rgba(0,0,0,0.2);
    }
    .tool-btn {
      display: flex; align-items: center; gap: 4px; padding: 8px 14px;
      border: none; border-radius: 8px; cursor: pointer; font-size: 13px;
      font-weight: 600; transition: all 0.15s; background: #F8FAFC; color: #334155;
    }
    .tool-btn:hover { background: #E2E8F0; }
    .tool-btn.active { background: #16A34A; color: white; }
    .tool-btn.danger { background: #FEE2E2; color: #DC2626; }
    .tool-hint {
      position: absolute; bottom: 72px; left: 50%; transform: translateX(-50%);
      background: rgba(0,0,0,0.8); color: white; padding: 8px 16px; border-radius: 8px;
      font-size: 13px; white-space: nowrap; z-index: 10; pointer-events: none;
    }

    .split-preview {
      position: absolute; top: 60px; right: 12px; z-index: 10;
      background: rgba(255,255,255,0.95); border-radius: 10px; padding: 12px;
      box-shadow: 0 2px 12px rgba(0,0,0,0.15); max-width: 240px;
    }
    .split-part {
      display: flex; align-items: center; justify-content: space-between; gap: 8px;
      padding: 8px; border-radius: 6px; margin-bottom: 6px; cursor: pointer;
      border: 2px solid transparent; transition: all 0.15s;
    }
    .split-part:hover { border-color: #16A34A; }
    .split-part-label { font-size: 13px; font-weight: 600; color: #0F172A; }
    .split-part-area { font-size: 12px; color: #475569; }
    .split-part-btn { padding: 4px 12px; border: none; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; }
    .split-part-btn.keep { background: #16A34A; color: white; }
  </style>
</head>
<body>
  <div id="map"></div>

  <div class="layer-panel" id="layerPanel">
    <div class="layer-header">
      <span class="layer-title">Layers</span>
      <button class="layer-toggle-btn" onclick="togglePanel()" title="Toggle layers">&#9776;</button>
    </div>
    <div class="layer-items" id="layerItems">
      <div class="layer-item" data-layer="parcels" onclick="toggleLayer('parcels')">
        <div class="layer-dot" style="background:#FFD700;border:1px solid #000"></div>Parcels
      </div>
      <div class="layer-item" data-layer="neighbors-l1" onclick="toggleLayer('neighbors-l1')">
        <div class="layer-dot" style="background:#4A90E2"></div>L1 Neighbors
      </div>
      <div class="layer-item" data-layer="neighbors-l2" onclick="toggleLayer('neighbors-l2')">
        <div class="layer-dot" style="background:#7B9ACC"></div>L2 Neighbors
      </div>
      <div class="layer-item" data-layer="neighbors-l3" onclick="toggleLayer('neighbors-l3')">
        <div class="layer-dot" style="background:#A8B8D8"></div>L3 Neighbors
      </div>
      <div class="layer-item" data-layer="photos" onclick="toggleLayer('photos')">
        <div class="layer-dot" style="background:#3B82F6"></div>Photos
      </div>
      <div class="layer-item" data-layer="voice" onclick="toggleLayer('voice')">
        <div class="layer-dot" style="background:#8B5CF6"></div>Voice Notes
      </div>
      <div class="layer-item" data-layer="notes" onclick="toggleLayer('notes')">
        <div class="layer-dot" style="background:#F59E0B"></div>Field Notes
      </div>
    </div>
  </div>

  <div class="tool-panel" id="toolPanel">
    <button class="tool-btn" id="scissorBtn" onclick="activateScissor()">&#9986; Split</button>
    <button class="tool-btn" id="mergeBtn" onclick="activateMerge()">&#10697; Merge</button>
  </div>
  <div class="tool-hint" id="toolHint" style="display:none"></div>
  <div class="split-preview" id="splitPreview" style="display:none"></div>

  <script>
    mapboxgl.accessToken = '${mapboxToken}';

    const map = new mapboxgl.Map({
      container: 'map',
      style: '${styleUrl}',
      center: [${center[0]}, ${center[1]}],
      zoom: ${zoom},
      attributionControl: false,
    });

    const layerVisibility = {
      'parcels': true, 'neighbors-l1': true, 'neighbors-l2': true, 'neighbors-l3': true,
      'photos': true, 'voice': true, 'notes': true
    };

    const mediaMarkers = { photos: [], voice: [], notes: [] };
    let activePopup = null;

    const tierColors = {
      1: { fill: '#4A90E2', line: '#2E5C8A', outline: '#1A3A5C' },
      2: { fill: '#7B9ACC', line: '#5A7AA3', outline: '#3A5A7A' },
      3: { fill: '#A8B8D8', line: '#8797B7', outline: '#677797' },
    };

    function togglePanel() {
      document.getElementById('layerPanel').classList.toggle('collapsed');
    }

    function toggleLayer(layerId) {
      layerVisibility[layerId] = !layerVisibility[layerId];
      const el = document.querySelector('[data-layer="' + layerId + '"]');
      if (el) el.classList.toggle('off', !layerVisibility[layerId]);

      if (layerId === 'parcels') {
        setLayerVis('parcels-primary-line', layerVisibility[layerId]);
        setLayerVis('parcels-primary-fill', layerVisibility[layerId]);
        setLayerVis('parcels-primary-outline', layerVisibility[layerId]);
      } else if (layerId.startsWith('neighbors-l')) {
        const tier = layerId.replace('neighbors-l', '');
        setLayerVis('parcels-neighbors-l' + tier + '-fill', layerVisibility[layerId]);
        setLayerVis('parcels-neighbors-l' + tier + '-line', layerVisibility[layerId]);
        setLayerVis('parcels-neighbors-l' + tier + '-outline', layerVisibility[layerId]);
      } else {
        const key = layerId === 'photos' ? 'photos' : layerId === 'voice' ? 'voice' : 'notes';
        mediaMarkers[key].forEach(m => { m.getElement().style.display = layerVisibility[layerId] ? '' : 'none'; });
      }
    }

    function setLayerVis(id, visible) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }

    function clearParcels() {
      ['parcels-primary-line','parcels-primary-fill','parcels-primary-outline'].forEach(l => {
        if (map.getLayer(l)) map.removeLayer(l);
      });
      if (map.getSource('parcels-primary')) map.removeSource('parcels-primary');
      for (let t = 1; t <= 3; t++) {
        ['fill','line','outline'].forEach(s => {
          const id = 'parcels-neighbors-l' + t + '-' + s;
          if (map.getLayer(id)) map.removeLayer(id);
        });
        const src = 'parcels-neighbors-l' + t;
        if (map.getSource(src)) map.removeSource(src);
      }
    }

    function showParcels(parcels, neighborsByTier) {
      clearParcels();
      if (!parcels || parcels.length === 0) return;

      // Primary parcels
      const primaryGeo = { type: 'FeatureCollection', features: parcels.map(p => ({
        type: 'Feature', geometry: p.geometry, properties: { id: p.id, name: p.name || '' }
      }))};

      map.addSource('parcels-primary', { type: 'geojson', data: primaryGeo });
      map.addLayer({ id: 'parcels-primary-fill', type: 'fill', source: 'parcels-primary',
        paint: { 'fill-color': '#FFD700', 'fill-opacity': 0.12 } });
      map.addLayer({ id: 'parcels-primary-outline', type: 'line', source: 'parcels-primary',
        paint: { 'line-color': '#000000', 'line-width': 2.5 } });
      map.addLayer({ id: 'parcels-primary-line', type: 'line', source: 'parcels-primary',
        paint: { 'line-color': '#FFD700', 'line-width': 2 } });

      // Neighbor tiers
      if (neighborsByTier) {
        for (let tier = 3; tier >= 1; tier--) {
          const neighbors = neighborsByTier[tier];
          if (!neighbors || neighbors.length === 0) continue;
          const tc = tierColors[tier];
          const geo = { type: 'FeatureCollection', features: neighbors.map(n => ({
            type: 'Feature', geometry: n.geometry, properties: { id: n.id, name: n.name || '', tier: tier }
          }))};
          const src = 'parcels-neighbors-l' + tier;
          map.addSource(src, { type: 'geojson', data: geo });
          map.addLayer({ id: src + '-fill', type: 'fill', source: src,
            paint: { 'fill-color': tc.fill, 'fill-opacity': 0.08 } });
          map.addLayer({ id: src + '-outline', type: 'line', source: src,
            paint: { 'line-color': tc.outline, 'line-width': 1.5 } });
          map.addLayer({ id: src + '-line', type: 'line', source: src,
            paint: { 'line-color': tc.line, 'line-width': 1 } });
        }
      }

      // Fit to primary parcels
      try {
        const bounds = new mapboxgl.LngLatBounds();
        primaryGeo.features.forEach(f => {
          const coords = f.geometry.type === 'Polygon' ? f.geometry.coordinates[0]
            : f.geometry.type === 'MultiPolygon' ? f.geometry.coordinates.flat(1).flat(0)
            : [];
          if (Array.isArray(coords)) coords.forEach(c => {
            if (Array.isArray(c) && c.length >= 2) bounds.extend(c);
          });
        });
        if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
      } catch(e) {}
    }

    function clearMediaMarkers(type) {
      if (mediaMarkers[type]) {
        mediaMarkers[type].forEach(m => m.remove());
        mediaMarkers[type] = [];
      }
    }

    function addMediaPins(pins) {
      clearMediaMarkers('photos');
      clearMediaMarkers('voice');
      clearMediaMarkers('notes');
      if (!pins) return;

      (pins.photos || []).forEach(p => {
        if (!p.longitude || !p.latitude) return;
        const el = document.createElement('div');
        el.className = 'media-marker photo';
        el.innerHTML = '&#128247;';
        el.onclick = () => showMediaPopup(p, 'photo');
        const marker = new mapboxgl.Marker({ element: el }).setLngLat([p.longitude, p.latitude]).addTo(map);
        if (!layerVisibility.photos) el.style.display = 'none';
        mediaMarkers.photos.push(marker);
      });

      (pins.voiceNotes || []).forEach(v => {
        if (!v.longitude || !v.latitude) return;
        const el = document.createElement('div');
        el.className = 'media-marker voice';
        el.innerHTML = '&#127908;';
        el.onclick = () => showMediaPopup(v, 'voice');
        const marker = new mapboxgl.Marker({ element: el }).setLngLat([v.longitude, v.latitude]).addTo(map);
        if (!layerVisibility.voice) el.style.display = 'none';
        mediaMarkers.voice.push(marker);
      });

      (pins.fieldNotes || []).forEach(n => {
        if (!n.longitude || !n.latitude) return;
        const el = document.createElement('div');
        el.className = 'media-marker note';
        el.innerHTML = '&#128221;';
        el.onclick = () => showMediaPopup(n, 'note');
        const marker = new mapboxgl.Marker({ element: el }).setLngLat([n.longitude, n.latitude]).addTo(map);
        if (!layerVisibility.notes) el.style.display = 'none';
        mediaMarkers.notes.push(marker);
      });
    }

    function showMediaPopup(item, type) {
      if (activePopup) activePopup.remove();

      let html = '<div class="popup-card">';
      if (type === 'photo') {
        if (item.thumbnail_data) {
          html += '<img class="popup-img" src="data:image/jpeg;base64,' + item.thumbnail_data + '" />';
        }
        html += '<div class="popup-type" style="color:#3B82F6">Photo</div>';
        if (item.caption) html += '<div class="popup-title">' + esc(item.caption) + '</div>';
        html += '<div class="popup-meta">' + esc(item.filename || '') + '</div>';
        html += '<div class="popup-meta">' + formatDate(item.created_at) + '</div>';
        html += '<div class="popup-actions"><button class="popup-btn popup-btn-primary" onclick="pinAction(\\'view\\',\\'photo\\',' + item.id + ')">View Full</button>';
        html += '<button class="popup-btn popup-btn-danger" onclick="pinAction(\\'delete\\',\\'photo\\',' + item.id + ')">Delete</button></div>';
      } else if (type === 'voice') {
        html += '<div class="popup-type" style="color:#8B5CF6">Voice Note</div>';
        if (item.caption) html += '<div class="popup-title">' + esc(item.caption) + '</div>';
        const dur = item.duration_ms ? Math.round(item.duration_ms / 1000) + 's' : '';
        html += '<div class="popup-body">' + (dur ? 'Duration: ' + dur : '') + '</div>';
        html += '<div class="popup-meta">' + (item.recorder_name || '') + ' &middot; ' + formatDate(item.created_at) + '</div>';
        html += '<div class="popup-actions"><button class="popup-btn popup-btn-primary" onclick="pinAction(\\'play\\',\\'voice\\',' + item.id + ')">Play</button>';
        html += '<button class="popup-btn popup-btn-danger" onclick="pinAction(\\'delete\\',\\'voice\\',' + item.id + ')">Delete</button></div>';
      } else {
        const catColors = {observation:'#16A34A',condition:'#F97316',boundary:'#8B5CF6',access:'#06B6D4',water:'#3B82F6',timber:'#16A34A',soil:'#92400E',wildlife:'#D97706',other:'#94A3B8'};
        const catColor = catColors[item.category] || '#94A3B8';
        html += '<div class="popup-category" style="background:' + catColor + '20;color:' + catColor + '">' + esc(item.category) + '</div>';
        if (item.title) html += '<div class="popup-title">' + esc(item.title) + '</div>';
        html += '<div class="popup-body">' + esc(item.body || '').substring(0, 200) + '</div>';
        html += '<div class="popup-meta">' + (item.author_name || '') + ' &middot; ' + formatDate(item.created_at) + '</div>';
        html += '<div class="popup-actions"><button class="popup-btn popup-btn-primary" onclick="pinAction(\\'edit\\',\\'note\\',' + item.id + ')">Edit</button>';
        html += '<button class="popup-btn popup-btn-danger" onclick="pinAction(\\'delete\\',\\'note\\',' + item.id + ')">Delete</button></div>';
      }
      html += '</div>';

      activePopup = new mapboxgl.Popup({ closeOnClick: true, maxWidth: '280px', offset: 20 })
        .setLngLat([item.longitude, item.latitude])
        .setHTML(html)
        .addTo(map);
    }

    function pinAction(action, type, id) {
      parent.postMessage({ type: 'media_pin_action', action, mediaType: type, id }, '*');
    }

    function esc(str) { return String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
    function formatDate(d) { try { return new Date(d).toLocaleDateString(); } catch(e) { return ''; } }

    // Message handler
    window.addEventListener('message', function(e) {
      const data = e.data;
      if (!data || !data.type) return;

      switch (data.type) {
        case 'set_parcels':
          showParcels(data.parcels || [], data.neighborsByTier || {});
          break;
        case 'set_media_pins':
          addMediaPins(data.pins);
          break;
        case 'fly_to':
          map.flyTo({ center: [data.lng, data.lat], zoom: data.zoom || 15 });
          break;
        case 'change_basemap':
          map.setStyle(data.styleUrl);
          break;
      }
    });

    // ===================== SPLIT (SCISSOR) TOOL =====================
    let scissorMode = false;
    let scissorPoints = [];
    let mergeMode = false;
    let mergeSelected = [];
    let splitSourceId = null;

    function activateScissor() {
      if (scissorMode) { deactivateTools(); return; }
      deactivateTools();
      scissorMode = true;
      scissorPoints = [];
      document.getElementById('scissorBtn').classList.add('active');
      showHint('Click two points on opposite sides of the parcel to draw a split line');
      map.getCanvas().style.cursor = 'crosshair';

      // Identify primary parcel feature for splitting
      if (savedParcels && savedParcels.length > 0) {
        splitSourceId = savedParcels[0].id;
      }
    }

    function activateMerge() {
      if (mergeMode) { deactivateTools(); return; }
      deactivateTools();
      mergeMode = true;
      mergeSelected = [];
      document.getElementById('mergeBtn').classList.add('active');
      showHint('Click on parcels to select them for merging (at least 2). Then press Confirm.');
      map.getCanvas().style.cursor = 'pointer';
    }

    function deactivateTools() {
      scissorMode = false; mergeMode = false;
      scissorPoints = []; mergeSelected = [];
      splitSourceId = null;
      document.getElementById('scissorBtn').classList.remove('active');
      document.getElementById('mergeBtn').classList.remove('active');
      document.getElementById('splitPreview').style.display = 'none';
      hideHint();
      map.getCanvas().style.cursor = '';
      if (map.getLayer('scissor-line')) map.removeLayer('scissor-line');
      if (map.getSource('scissor-line')) map.removeSource('scissor-line');
      if (map.getLayer('scissor-points')) map.removeLayer('scissor-points');
      if (map.getSource('scissor-points')) map.removeSource('scissor-points');
      if (map.getLayer('split-parts-0')) map.removeLayer('split-parts-0');
      if (map.getSource('split-parts-0')) map.removeSource('split-parts-0');
      if (map.getLayer('split-parts-1')) map.removeLayer('split-parts-1');
      if (map.getSource('split-parts-1')) map.removeSource('split-parts-1');
      if (map.getLayer('merge-highlight')) map.removeLayer('merge-highlight');
      if (map.getSource('merge-highlight')) map.removeSource('merge-highlight');
    }

    function showHint(msg) { const h = document.getElementById('toolHint'); h.textContent = msg; h.style.display = ''; }
    function hideHint() { document.getElementById('toolHint').style.display = 'none'; }

    map.on('click', function(e) {
      if (scissorMode) handleScissorClick(e);
      else if (mergeMode) handleMergeClick(e);
    });

    function handleScissorClick(e) {
      scissorPoints.push([e.lngLat.lng, e.lngLat.lat]);
      drawScissorLine();

      if (scissorPoints.length >= 2) {
        showHint('Processing split...');
        const splitLine = { type: 'LineString', coordinates: scissorPoints };
        parent.postMessage({ type: 'split_request', featureId: splitSourceId, splitLine }, '*');
      } else {
        showHint('Click a second point to complete the split line');
      }
    }

    function drawScissorLine() {
      const geo = { type: 'FeatureCollection', features: [{
        type: 'Feature', geometry: { type: 'LineString', coordinates: scissorPoints.length > 1 ? scissorPoints : [...scissorPoints, scissorPoints[0]] },
        properties: {}
      }]};
      const ptGeo = { type: 'FeatureCollection', features: scissorPoints.map(c => ({
        type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {}
      }))};

      if (map.getSource('scissor-line')) { map.getSource('scissor-line').setData(geo); }
      else {
        map.addSource('scissor-line', { type: 'geojson', data: geo });
        map.addLayer({ id: 'scissor-line', type: 'line', source: 'scissor-line',
          paint: { 'line-color': '#DC2626', 'line-width': 3, 'line-dasharray': [4, 3] } });
      }
      if (map.getSource('scissor-points')) { map.getSource('scissor-points').setData(ptGeo); }
      else {
        map.addSource('scissor-points', { type: 'geojson', data: ptGeo });
        map.addLayer({ id: 'scissor-points', type: 'circle', source: 'scissor-points',
          paint: { 'circle-color': '#DC2626', 'circle-radius': 6, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } });
      }
    }

    function showSplitParts(parts) {
      hideHint();
      const colors = ['#3B82F6', '#F59E0B'];
      parts.forEach((part, i) => {
        const srcId = 'split-parts-' + i;
        if (map.getSource(srcId)) map.removeSource(srcId);
        map.addSource(srcId, { type: 'geojson', data: { type: 'Feature', geometry: part.geometry, properties: {} } });
        if (map.getLayer(srcId)) map.removeLayer(srcId);
        map.addLayer({ id: srcId, type: 'fill', source: srcId,
          paint: { 'fill-color': colors[i], 'fill-opacity': 0.35 } });
      });

      let html = '<div style="font-size:12px;font-weight:600;margin-bottom:8px;color:#0F172A">Select which part to keep:</div>';
      parts.forEach((part, i) => {
        const col = colors[i];
        html += '<div class="split-part" style="background:' + col + '15" onclick="selectSplitPart(' + i + ')">';
        html += '<div><div class="split-part-label" style="color:' + col + '">Part ' + (i + 1) + '</div>';
        html += '<div class="split-part-area">' + part.area_acres.toFixed(2) + ' acres</div></div>';
        html += '<button class="split-part-btn keep" onclick="event.stopPropagation();selectSplitPart(' + i + ')">Keep</button></div>';
      });
      html += '<div style="text-align:center;margin-top:4px"><button class="tool-btn danger" onclick="deactivateTools()" style="width:100%">Cancel</button></div>';
      document.getElementById('splitPreview').innerHTML = html;
      document.getElementById('splitPreview').style.display = '';
    }

    function selectSplitPart(index) {
      parent.postMessage({ type: 'split_confirm', partIndex: index }, '*');
      deactivateTools();
    }

    function handleMergeClick(e) {
      // Check which parcel was clicked
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['parcels-primary-fill', 'parcels-neighbors-l1-fill', 'parcels-neighbors-l2-fill', 'parcels-neighbors-l3-fill'].filter(l => map.getLayer(l))
      });

      if (features.length > 0) {
        const fId = features[0].properties.id;
        if (mergeSelected.includes(fId)) {
          mergeSelected = mergeSelected.filter(id => id !== fId);
        } else {
          mergeSelected.push(fId);
        }
        highlightMergeSelection();
        showHint(mergeSelected.length + ' parcels selected. ' + (mergeSelected.length >= 2 ? 'Press Merge to confirm.' : 'Select at least 2.'));
        if (mergeSelected.length >= 2) {
          document.getElementById('mergeBtn').innerHTML = '&#10697; Merge (' + mergeSelected.length + ')';
          document.getElementById('mergeBtn').onclick = function() { confirmMerge(); };
        }
      }
    }

    function highlightMergeSelection() {
      // Collect geometries of selected features from all sources
      const allFeatures = [];
      ['parcels-primary', 'parcels-neighbors-l1', 'parcels-neighbors-l2', 'parcels-neighbors-l3'].forEach(src => {
        if (!map.getSource(src)) return;
        const data = map.getSource(src)._data || map.getSource(src)._options?.data;
        if (data && data.features) {
          data.features.forEach(f => {
            if (mergeSelected.includes(f.properties.id)) allFeatures.push(f);
          });
        }
      });

      const geo = { type: 'FeatureCollection', features: allFeatures };
      if (map.getSource('merge-highlight')) { map.getSource('merge-highlight').setData(geo); }
      else {
        map.addSource('merge-highlight', { type: 'geojson', data: geo });
        map.addLayer({ id: 'merge-highlight', type: 'fill', source: 'merge-highlight',
          paint: { 'fill-color': '#16A34A', 'fill-opacity': 0.3 } });
      }
    }

    function confirmMerge() {
      parent.postMessage({ type: 'merge_request', featureIds: mergeSelected }, '*');
      deactivateTools();
    }

    // Message handler
    window.addEventListener('message', function(e) {
      const data = e.data;
      if (!data || !data.type) return;

      switch (data.type) {
        case 'set_parcels':
          showParcels(data.parcels || [], data.neighborsByTier || {});
          break;
        case 'set_media_pins':
          addMediaPins(data.pins);
          break;
        case 'fly_to':
          map.flyTo({ center: [data.lng, data.lat], zoom: data.zoom || 15 });
          break;
        case 'change_basemap':
          map.setStyle(data.styleUrl);
          break;
        case 'split_result':
          if (data.success) showSplitParts(data.parts);
          else { showHint(data.error || 'Split failed'); setTimeout(hideHint, 3000); }
          break;
      }
    });

    map.on('load', function() {
      parent.postMessage({ type: 'map_ready' }, '*');
    });

    // Re-add data after style change
    let savedParcels = null;
    let savedNeighbors = null;
    let savedPins = null;
    const origShowParcels = showParcels;
    showParcels = function(p, n) { savedParcels = p; savedNeighbors = n; origShowParcels(p, n); };
    const origAddPins = addMediaPins;
    addMediaPins = function(p) { savedPins = p; origAddPins(p); };
    map.on('style.load', function() {
      if (savedParcels) origShowParcels(savedParcels, savedNeighbors);
      if (savedPins) origAddPins(savedPins);
    });
  </script>
</body>
</html>`;
}
